/**
 * The seven date functions' response processors (LSEG's Date*ResProcessor),
 * and `layAdFinResponse` (alias `layDateResponse`), which turns LSEG's answer
 * to a date or FX function into the add-in's Table.
 */

import { AdFinInputContract } from "./baseProcessor";
import { ADFIN_FUNCTIONS, AdFinFn, DateFn } from "./processors";
import { AdFinValidationError } from "./utils";
import {
  ApiError,
  BaseAdFinResProcessor,
  ClientResponse,
  errorMessage,
  requestorError,
  ResponseError,
  ResponseHeader,
} from "./responseBase";
import { FX_RESPONSE_PROCESSORS } from "./fxResponses";
import { Table } from "../lib/layout";
import { toTable } from "../lib/grids";
import { DateSerial, isoToSerial, NaError } from "../lib/refinitiv";

/** The answer's item: `data[0]`, or the answer itself. */
// LSEG: Date*ResProcessor.getDataFromResponse (`e?.data?.[0] ?? e`, the same in all seven)
function item(response: unknown): Record<string, any> {
  const r = response as { data?: unknown[] } | null;
  return (r?.data?.[0] ?? r) as Record<string, any>;
}

const orEmpty = (v: unknown): unknown => v ?? "";

/** A list of calendars as one text, "EMU, FRA". [A] A value that is not a list is empty (LSEG's `.join` would throw). */
const joinCalendars = (v: unknown): unknown => (Array.isArray(v) ? v.join(", ") : undefined);

// LSEG: DateIsWDResProcessor_53340
export class DateIsWDResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [
      { name: "IsWorkingDay", type: "Boolean" },
      { name: "Calendars", type: "String" },
    ];
  }

  getDataFromResponse(response: unknown, contract: AdFinInputContract): ClientResponse {
    if (!response) return this.badSyntax("DateIsWD");
    const i = item(response);
    return this.getDataRows({ headers: this.getHeaders(), data: [[orEmpty(i?.isWorkingDay), orEmpty(joinCalendars(i?.calendars))]] }, contract);
  }
}

// LSEG: DateLastWDResProcessor_87976
export class DateLastWDResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [{ name: "LastWorkingDay", type: "Date" }];
  }

  getDataFromResponse(response: unknown, contract: AdFinInputContract): ClientResponse {
    if (!response) return this.badSyntax("DateLastWD");
    const i = item(response);
    return this.getDataRows({ headers: this.getHeaders(), data: [[orEmpty(i?.lastWorkingDay)]] }, contract);
  }
}

// LSEG: DateListHolidaysResProcessor_50152
export class DateListHolidaysResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [
      { name: "Date", type: "Date" },
      { name: "Name", type: "String" },
      { name: "Calendars", type: "String" },
    ];
  }

  getDataFromResponse(response: unknown, contract: AdFinInputContract): ClientResponse {
    if (!response) return this.badSyntax("DateListHolidays");
    const i = item(response);
    const data: unknown[][] = [];
    // [A] A `holidays` that is not a list gives no rows (LSEG's `.forEach` would throw).
    if (Array.isArray(i?.holidays)) {
      i.holidays.forEach((h: any) => {
        if (h) data.push([orEmpty(h.date), orEmpty(h.name), orEmpty(joinCalendars(h.calendars))]);
      });
    }
    return this.getDataRows({ headers: this.getHeaders(), data }, contract);
  }
}

// LSEG: DateAdjToWDResProcessor_85215
export class DateAdjToWDResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [{ name: "AdjustedDate", type: "String" }];
  }

  getDataFromResponse(response: unknown, contract: AdFinInputContract): ClientResponse {
    if (!response) return this.badSyntax("DateAdjToWD");
    const i = item(response);
    return this.getDataRows({ headers: this.getHeaders(), data: [[orEmpty(i?.adjustedDate)]] }, contract);
  }
}

// LSEG: DateCalcPeriodResProcessor_62668
export class DateCalcPeriodResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [
      { name: "StartDate", type: "String" },
      { name: "EndDate", type: "String" },
    ];
  }

  getDataFromResponse(response: unknown, contract: AdFinInputContract): ClientResponse {
    if (!response) return this.badSyntax("DateCalcPeriod");
    const i = item(response);
    return this.getDataRows({ headers: this.getHeaders(), data: [[orEmpty(i?.startDate), orEmpty(i?.endDate)]] }, contract);
  }
}

// LSEG: DateCountPeriodsResProcessor_75544
export class DateCountPeriodsResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [
      { name: "Count", type: "Number" },
      { name: "Tenor", type: "String" },
    ];
  }

  getDataFromResponse(response: unknown, contract: AdFinInputContract): ClientResponse {
    if (!response) return this.badSyntax("DateCountPeriods");
    const i = item(response);
    return this.getDataRows({ headers: this.getHeaders(), data: [[orEmpty(i?.count), orEmpty(i?.tenor)]] }, contract);
  }
}

// LSEG: DateFindDateResProcessor_9830
export class DateFindDateResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [{ name: "Date", type: "Date" }];
  }

  /** A processingInformation in the answer adds its column. */
  getDataFromResponse(response: unknown, contract: AdFinInputContract): ClientResponse {
    if (!response) return this.badSyntax("DateFindDate");
    const i = item(response);
    const headers = this.getHeaders();
    const data: unknown[][] = [[orEmpty(i?.date)]];
    if (i?.processingInformation) {
      headers.push({ name: "ProcessingInformation", type: "String" });
      data[0].push(i.processingInformation);
    }
    return this.getDataRows({ headers, data }, contract);
  }
}

// LSEG: InteprResProcessor (sic)
export class InterpResProcessor extends BaseAdFinResProcessor {
  getHeaders(): ResponseHeader[] {
    return [{ name: "Y value", type: "Number" }];
  }

  /** The answer's `value` (a NaN stays NaN: the layout shows it as #N/A). */
  getDataFromResponse(response: unknown, contract: AdFinInputContract): ClientResponse {
    if (!response) return this.badSyntax("Interp");
    const i = item(response);
    return this.getDataRows({ headers: this.getHeaders(), data: [[orEmpty(i?.value)]] }, contract);
  }
}

/** Each date function's response processor. */
export const DATE_RESPONSE_PROCESSORS: Record<DateFn, new () => BaseAdFinResProcessor> = {
  DateIsWD: DateIsWDResProcessor,
  DateAdjToWD: DateAdjToWDResProcessor,
  DateLastWD: DateLastWDResProcessor,
  DateListHolidays: DateListHolidaysResProcessor,
  DateCalcPeriod: DateCalcPeriodResProcessor,
  DateCountPeriods: DateCountPeriodsResProcessor,
  DateFindDate: DateFindDateResProcessor,
};

/**
 * What LSEG's response processor gets for one request of a batched answer
 * (`{ data: [item, ...] }`): `{ data: [item] }`, or the error the item (or
 * its absence) becomes, with LSEG's executor's text in `message`.
 * [A] An `error` for the whole answer is shown the same way.
 */
// LSEG: SyncBatchRequestExecutor (80918).handleResponse, then BaseAFExecutor (41359).execute's catch
function executorResult(raw: unknown): { data: unknown[] } | { error: { message: string } } {
  const body = raw as { data?: unknown; error?: ApiError } | null;
  if (body?.error) return { error: { message: errorMessage(requestorError(body.error)) } };
  const items = Array.isArray(body?.data) ? body!.data : [];
  if (!items.length) return { error: { message: errorMessage(requestorError({ message: "No response for function" })) } };
  const first = items[0] as { error?: ApiError } | null;
  if (first?.error) return { error: { message: errorMessage(requestorError(first.error)) } };
  return { data: [first] };
}

/** ISO dates ("YYYY-MM-DD", or with "T00:00:00" left by modifyDataDates) as date serials, the way TR marks dates. */
// [A] LSEG's add-in hands Excel the ISO text and Excel's side shows it as a date (goldens show serials in the short-date format).
function markDate(v: unknown): unknown {
  if (typeof v !== "string" || !/^\d{4}-\d{2}-\d{2}(T00:00:00)?$/.test(v.trim())) return v;
  const serial = isoToSerial(v);
  return typeof serial === "number" ? new DateSerial(serial) : v;
}

/**
 * A cell as the table holds it: a date marked as one, and a NaN (Interp's invalid input)
 * as Excel's #N/A error, which is what Excel shows for a NaN it is handed.
 */
export function markCell(v: unknown): unknown {
  return typeof v === "number" && Number.isNaN(v) ? new NaError() : markDate(v);
}

/**
 * The Excel grid without a Dest: the formula cell's value at the top-left,
 * each block at its place, missing cells empty.
 */
export function toGrid(response: ClientResponse): unknown[][] {
  const grid: unknown[][] = [[response.callerValue]];
  for (const { position, data } of response.positionedData) {
    data.forEach((row, r) =>
      row.forEach((v, c) => {
        const line = (grid[position.row + r] = grid[position.row + r] ?? []);
        line[position.column + c] = v;
      }),
    );
  }
  const width = Math.max(...grid.map((row) => (row ? row.length : 0)));
  for (let r = 0; r < grid.length; r++) {
    const row = grid[r] ?? [];
    grid[r] = Array.from({ length: width }, (_, c) => (row[c] === undefined ? "" : row[c]));
  }
  return grid;
}

/** Each date, FX and Interp function's response processor. */
export const ADFIN_RESPONSE_PROCESSORS: Record<AdFinFn, new () => BaseAdFinResProcessor> = {
  ...DATE_RESPONSE_PROCESSORS,
  ...FX_RESPONSE_PROCESSORS,
  Interp: InterpResProcessor,
};

/**
 * Whether the formula cell's value is empty, so that LSEG's Excel side shows
 * "Updated at <time>" there instead: undefined, null, "", an empty list or an object with no values.
 */
// LSEG: 4269.isEmpty, as DispatchHandler (98867)._setResultWithStamp uses it on the caller value
export function isEmptyCallerValue(v: unknown): boolean {
  if (v === undefined) return true;
  if (typeof v === "object") return !v || (Array.isArray(v) ? v.length === 0 : Object.values(v).length === 0);
  return typeof v === "string" && v.length === 0;
}

/**
 * LSEG's answer to `RDP.<fn>(...args)` (a date or FX function) as the grid
 * LSEG's Excel add-in shows without a Dest: dates as date serials marked as
 * TR marks them, an error (or invalid arguments) as one text cell, and an
 * empty formula-cell value as the empty corner, where the static writer puts
 * "Updated at". `args` are the Excel arguments in LSEG's order, as for
 * `buildAdFinRequest`; `raw` is the analytics answer, `{ data: [item] }`.
 */
export function layAdFinResponse(fn: AdFinFn, args: unknown[], raw: unknown): Table {
  const processor = new ADFIN_FUNCTIONS[fn].processor(args);
  let request: Record<string, unknown>;
  try {
    request = processor.getAdFinRequestInput();
  } catch (e) {
    if (e instanceof AdFinValidationError) return toTable([[e.message]]);
    throw e;
  }
  const resProcessor = new ADFIN_RESPONSE_PROCESSORS[fn]();
  let response: ClientResponse;
  try {
    // LSEG: AdFinService (45048).processSingleResponse: response(answer, contract, request item)
    response = resProcessor.response(executorResult(raw), processor.inputContract, request);
  } catch (e) {
    // LSEG: AdFinService (45048).processSingleResponse's catch: a failure while laying out (an unknown CH/RH
    // throws "Invalid display options!", a missing FxInfo `styles` a TypeError) goes back through
    // `response({ error })`. An Error's message is not enumerable, so the error keeps none of it and the
    // caller value is undefined: the formula cell shows "Updated at" (golden fx_f_rh, Options "CH:Yes").
    response = resProcessor.response({ error: { ...(e as object) } as ResponseError }, processor.inputContract, request);
  }
  const grid = trimTrailingBlanks(toGrid(response).map((row) => row.map(markCell)));
  return toTable(grid, isEmptyCallerValue(response.callerValue));
}

/** The date functions' name for `layAdFinResponse`. */
export const layDateResponse = layAdFinResponse;

const isBlank = (v: unknown): boolean => v === null || v === undefined || v === "";

/**
 * [A] The grid without its trailing columns that are blank in every row and
 * its trailing rows (below the first) that are wholly blank, so a one-value
 * answer is one cell. LSEG's processor writes "" there (DateIsWD without
 * `calendars`: "" at C2), but whether its Excel side clears a user's cell
 * with it is not captured; a blank written by the static writer would clear it.
 * The first row and leading or inner cells are never trimmed; the formula cell always stays.
 */
export function trimTrailingBlanks(grid: unknown[][]): unknown[][] {
  let height = grid.length;
  while (height > 1 && grid[height - 1].every(isBlank)) height--;
  const rows = grid.slice(0, height);
  let width = Math.max(...rows.map((row) => row.length));
  while (width > 1 && rows.every((row) => isBlank(row[width - 1]))) width--;
  return rows.map((row) => row.slice(0, width));
}
